import { RootStore } from '@jobnimbus/web-state';
import { useCallback, useEffect, useState } from 'react';
import { CONTAINER_ID } from '../engage-constants';
import { SendMessage } from './iframe-communications.service';
import { resizeContainer, setInitialProfile } from './resize-handlers';

export let pendingRoutingRequest;

const ROUTING_REQUEST_INTERVAL_MS = 200;
export const ROUTING_REQUEST_MAX_ATTEMPTS = 25;

// lastRequestedPath is where the embedded app has been told to be; pendingRoutingPath
// is what the running interval is still delivering. They diverge whenever the app
// reports a move of its own, and a stale ROUTE_ACK must not settle a newer request.
let lastRequestedPath: string | null = null;
let pendingRoutingPath: string | null = null;

function settlePendingRoutingRequest() {
  if (!pendingRoutingRequest) return;

  clearInterval(pendingRoutingRequest);
  pendingRoutingRequest = undefined;
  pendingRoutingPath = null;
}

const pathnameOf = (path: string) =>
  new URL(path, window.location.origin).pathname;

export const getAndUpdateAppVisibilityState = (
  newUrl: string,
  hiddenRoutes: string[]
): boolean => {
  const containerElement = document.getElementById(CONTAINER_ID);
  const containerCurrentlyHidden = containerElement
    ? containerElement?.style?.display === 'none'
    : true;
  const newRouteShouldBeHidden = hiddenRoutes.some((el) => {
    return newUrl?.toLowerCase().includes(el?.toLowerCase());
  });

  if (!containerCurrentlyHidden) {
    if (!containerCurrentlyHidden && newRouteShouldBeHidden) {
      containerElement.style.display = 'none';
    } else if (containerCurrentlyHidden && !newRouteShouldBeHidden) {
      containerElement.style.display = 'block';
    }
  }

  return !newRouteShouldBeHidden;
};

export const HandleParentRoutingEvent = (
  event: Event,
  embeddedAppOrigin: string,
  rootStore: RootStore,
  hiddenRoutes: string[]
) => {
  const customEvent = event as CustomEvent;
  const verbose = rootStore.internalStore.debugEnabled;
  if (verbose)
    console.log('DEBUG: HandleParentRoutingEvent', event, embeddedAppOrigin);

  const isEmbeddedAppVisible = getAndUpdateAppVisibilityState(
    customEvent.detail.newUrl,
    hiddenRoutes
  );

  if (!isEmbeddedAppVisible) return;

  if (
    customEvent.detail.originalEvent?.state?.engageInitiated &&
    (customEvent.detail.originalEvent?.singleSpaTrigger === 'pushState' ||
      customEvent.detail.originalEvent?.singleSpaTrigger === 'replaceState')
  ) {
    return;
  }

  const path =
    window.location.pathname + window.location.search + window.location.hash;
  const accessToken = rootStore.userStore.accessToken;

  sendEmbeddedAppRoutingEvent(path, embeddedAppOrigin, verbose, accessToken);
};

const sendEmbeddedAppRoutingEvent = (
  path: string,
  embeddedAppOrigin: string,
  verbose: boolean,
  accessToken: string
) => {
  // The embedded app reloads itself for any request naming a path it is not already
  // on, so a redundant request costs the user their open conversation (FULFMNT-2031).
  if (path === lastRequestedPath) return;

  const message = {
    ROUTING_REQUEST: path,
    ACCESS_TOKEN: accessToken,
  };
  if (pendingRoutingRequest) clearInterval(pendingRoutingRequest);

  lastRequestedPath = path;
  pendingRoutingPath = path;
  let attempts = 0;

  // An app that never acknowledges — still loading, mid-navigation — would otherwise
  // be re-sent this request every 200ms for the life of the page.
  pendingRoutingRequest = setInterval(() => {
    SendMessage(message, embeddedAppOrigin, verbose);

    if (++attempts < ROUTING_REQUEST_MAX_ATTEMPTS) return;

    if (verbose)
      console.log(
        `Abandoning routing request after ${attempts} attempts:`,
        path
      );

    settlePendingRoutingRequest();
    lastRequestedPath = null;
  }, ROUTING_REQUEST_INTERVAL_MS);
};

export function HandleEngageMessages(
  event,
  rootStore: RootStore,
  hiddenRoutes: string[]
) {
  rootStore.internalStore.debugMessage(
    `wrapper for embedded app with ID ${CONTAINER_ID} received a message from the app`,
    event.data
  );

  console.log('engage-wrapper: received message from engage: %o', event);

  if (event.data.ENGAGE_ADDRESS) {
    if (
      getAndUpdateAppVisibilityState(window.location.pathname, hiddenRoutes)
    ) {
      const path = event.data.ENGAGE_ADDRESS;

      // The app reports its address from the document head of every page load,
      // before its ROUTING_REQUEST listener exists — so a report naming a different
      // pathname than the pending request means that request is still undelivered
      // and must keep retrying, while a matching report settles it. Either way the
      // reported address must not stay deduped against a later request.
      if (
        pendingRoutingPath === null ||
        pathnameOf(path) === pathnameOf(pendingRoutingPath)
      ) {
        settlePendingRoutingRequest();
      }
      lastRequestedPath = null;

      if (path === window.location.pathname + window.location.search) {
        return;
      }
      try {
        history.pushState({ engageInitiated: true }, '', path);
      } catch {
        console.log('Unable to pushState. Path: ', path);
      }
    }
  }

  if (event.data.ENGAGE_TITLE) {
    document.title = event.data.ENGAGE_TITLE;
  }

  if (event.data.ROUTE_ACK === pendingRoutingPath) {
    settlePendingRoutingRequest();
  }

  if (event.data.REQUEST_ACTION) {
    switch (event.data.REQUEST_ACTION.type) {
      case 'GET_ACCESS_TOKEN':
        SendMessage(
          { ACCESS_TOKEN: rootStore.userStore.accessToken },
          event.origin,
          rootStore.internalStore.debugEnabled
        );
        break;
      case 'GET_FEATURE_FLAGS':
        SendMessage(
          { FEATURE_FLAGS: rootStore.userStore.LDFlagSet || {} },
          event.origin,
          rootStore.internalStore.debugEnabled
        );
        break;
    }
  }

  if (event.data.CLEAR_PARAMETERS) {
    window.history.replaceState(
      { engageInitiated: true },
      document.title,
      window.location.pathname
    );
  }

  if (event.data.QUERY) {
    const { parameter, value } = event.data.QUERY;
    const query = `?${parameter}=${value}`;
    const url = window.location.pathname + query;
    window.history.replaceState({ engageInitiated: true }, document.title, url);
  }
}

export function addWindowEventListeners({
  engageOrigin,
  rootStore,
}: {
  engageOrigin: string;
  rootStore: RootStore;
}) {
  window.addEventListener('message', (event) => {
    if (event.origin !== engageOrigin) {
      return;
    }
    HandleEngageMessages(event, rootStore, []);
  });

  window.addEventListener('single-spa:routing-event', (event) => {
    HandleParentRoutingEvent(event, engageOrigin, rootStore, []);
  });

  window.addEventListener('resize', resizeContainer);

  setInitialProfile(rootStore.userStore.isLoggedIn);
}

export enum EngagePostMessageActions {
  OPEN_ATTACH_FILE_DIALOG = 'OPEN_ATTACH_FILE_DIALOG',
  OPEN_SAVE_FILE_MODAL = 'OPEN_SAVE_FILE_MODAL',
  OPEN_SELECT_JOB_MODAL = 'OPEN_SELECT_JOB_MODAL',
  OPEN_SAVE_TO_CONTACT_MODAL = 'OPEN_SAVE_TO_CONTACT_MODAL',
  OPEN_SAVE_TO_JOB_MODAL = 'OPEN_SAVE_TO_JOB_MODAL',
  OPEN_UPLOAD_FILE_FROM_COMPUTER = 'OPEN_UPLOAD_FILE_FROM_COMPUTER',
  SAVE_OPTIONS_POPUP = 'SAVE_OPTIONS_POPUP',
  UPLOAD_OPTIONS_POPUP = 'UPLOAD_OPTIONS_POPUP',
  OPEN_INBOX_FILTER = 'OPEN_INBOX_FILTER',
  REFRESH_INBOX_FILTER = 'REFRESH_INBOX_FILTER',
}

export function useIframeEngageActionListener({
  engageOrigin,
  action,
}: {
  engageOrigin: string;
  action: EngagePostMessageActions;
}) {
  const [eventBody, setEventBody] = useState(null);
  const messageListener = useCallback(
    (event) => {
      if (
        ![engageOrigin, window.location.origin].includes(event.origin) ||
        !event.data.REQUEST_ACTION ||
        event.data.REQUEST_ACTION.type !== action
      ) {
        return;
      }

      setEventBody({ ...event.data.REQUEST_ACTION });
    },
    [action, engageOrigin]
  );

  useEffect(() => {
    window.addEventListener('message', messageListener);

    return () => window.removeEventListener('message', messageListener);
  }, [eventBody, messageListener]);

  return eventBody;
}
